import type { DataSource, FieldSchema } from "../types/data.js";
import type { ReportTemplate } from "../types/template.js";

/**
 * A data model with nothing in it from anybody's data.
 *
 * `example` is the obvious one — and in BroadPaper Cloud it is the first real
 * value of the customer's column, which is exactly what must not leave. `enum`
 * goes too unless the host asks to keep it: a host's own enum is a schema
 * ("draft", "final"), but a list of values read out of a spreadsheet is data.
 */
export function redactDataModel(sources: DataSource[], opts: { keepEnums?: boolean } = {}): DataSource[] {
  const field = (f: FieldSchema): FieldSchema => {
    const out: FieldSchema = { type: f.type };
    if (f.label) out.label = f.label;
    if (f.description) out.description = f.description;
    if (f.format) out.format = f.format;
    if (f.currency) out.currency = f.currency;
    if (f.required) out.required = f.required;
    if (opts.keepEnums && f.enum) out.enum = [...f.enum];
    if (f.fields) out.fields = Object.fromEntries(Object.entries(f.fields).map(([k, v]) => [k, field(v)]));
    if (f.itemSchema) out.itemSchema = field(f.itemSchema);
    return out;
  };
  return sources.map((s) => {
    const out: DataSource = { id: s.id, label: s.label, schema: Object.fromEntries(Object.entries(s.schema).map(([k, v]) => [k, field(v)])) };
    if (s.description) out.description = s.description;
    return out;
  });
}

const ASSET = /^asset:\d+$/;
/** Long enough that no ordinary prop is caught; short enough that every embedded image is. */
const INLINE_LIMIT = 256;

/**
 * Replaces every embedded file in a template — a logo, an image — with
 * `asset:N`, and returns the map to put them back.
 *
 * A logo as a data URI is tens of kilobytes of base64: thousands of tokens the
 * model cannot read, paid for on every request, and a business's artwork sent
 * somewhere it did not need to go. The model keeps the placeholder where it
 * wants the image and `restoreAssets` swaps it back.
 */
export function redactAssets(template: ReportTemplate): { template: ReportTemplate; assets: Map<string, string> } {
  const assets = new Map<string, string>();
  const byValue = new Map<string, string>();
  const visit = (v: unknown): unknown => {
    if (typeof v === "string") {
      if (v.length > INLINE_LIMIT && /^data:/i.test(v)) {
        let key = byValue.get(v);
        if (!key) {
          key = `asset:${assets.size + 1}`;
          assets.set(key, v);
          byValue.set(v, key);
        }
        return key;
      }
      return v;
    }
    if (Array.isArray(v)) return v.map(visit);
    if (v && typeof v === "object") return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, visit(x)]));
    return v;
  };
  return { template: visit(template) as ReportTemplate, assets };
}

/** Puts back what `redactAssets` took out. A placeholder with no asset is reported, not guessed at. */
export function restoreAssets<T>(value: T, assets: Map<string, string>): { value: T; unknown: string[] } {
  const unknown: string[] = [];
  const visit = (v: unknown): unknown => {
    if (typeof v === "string" && ASSET.test(v)) {
      const hit = assets.get(v);
      if (hit === undefined) unknown.push(v);
      return hit ?? v;
    }
    if (Array.isArray(v)) return v.map(visit);
    if (v && typeof v === "object") return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, visit(x)]));
    return v;
  };
  return { value: visit(value) as T, unknown };
}

/** Every `asset:N` placeholder in a value. */
export function assetPlaceholders(value: unknown): Set<string> {
  const out = new Set<string>();
  const visit = (v: unknown) => {
    if (typeof v === "string" && ASSET.test(v)) out.add(v);
    else if (Array.isArray(v)) v.forEach(visit);
    else if (v && typeof v === "object") Object.values(v).forEach(visit);
  };
  visit(value);
  return out;
}
